/**
 * tab-backfill.ts — Phase 2 daemon boot backfill.
 *
 * Runs once on every daemon boot (idempotent). For each workspace:
 *   1. Resolves the canonical project key (git remote URL, or the
 *      `.appostle/appostleproj-<hex>` marker for non-git folders).
 *   2. Upserts the project to the auth-server via POST /daemon/sync/projects —
 *      passing the legacy `.appostle/project-id` dotfile value (if present) so
 *      the auth-server re-keys the existing row in place, then deletes the
 *      legacy dotfile.
 *   3. Migrates user-created stacks from workspace-stacks.json (non-starter ones).
 *   4. Migrates every non-internal agent to a Tab in the correct stack.
 *   5. Creates Tabs for session-share recipients in their "Shared with me" stacks.
 *
 * Design decisions baked in here:
 *   - The project key is machine-independent: git projects share the same key
 *     on every machine (the remote URL), non-git folders carry their marker
 *     file with the folder (Dropbox sync). The per-row `machineId` keeps
 *     RemoteProject rows per-host.
 *   - The four paseo-era starter stacks ("Copywriting", "Refactoring", "Design",
 *     "Feature build") are NOT migrated. The auth-server auto-creates a single
 *     "Sessions" user stack on project creation, which replaces them.
 *   - Internal agents (internal: true) are skipped — they are not user-visible
 *     until explicitly promoted, so they should not get Tabs.
 *   - Agents with no ownerUserId (pre-Phase-2c records) are attributed to the
 *     daemon's own owner user.
 *   - If anything goes wrong for a single workspace or agent, the error is logged
 *     and the backfill continues — a partial run is always recoverable on the
 *     next boot.
 */

import { promises as fs } from "node:fs";
import path from "node:path";
import type { Logger } from "pino";
import {
  deleteProjectMarker,
  readProjectMarker,
  peekCanonicalProjectId,
  resolveCanonicalProjectId,
} from "./git-remote-resolver.js";
import type { AgentStorage } from "./agent/agent-storage.js";
import type {
  PersistedWorkspaceRecord,
  ProjectRegistry,
  WorkspaceRegistry,
} from "./workspace-registry.js";
import { z } from "zod";

// ---- Starter stack names (paseo-era, NOT migrated) --------------------------

const STARTER_STACK_NAMES = new Set(["Copywriting", "Refactoring", "Design", "Feature build"]);

// ---- workspace-stacks.json schema (mirrors WorkspaceStacksService types) ----

const PersistedStackSchema = z.object({
  id: z.string(),
  name: z.string(),
  order: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const PersistedScopeRecordSchema = z.object({
  userId: z.string(),
  workspaceId: z.string(),
  stacks: z.array(PersistedStackSchema),
  activeStackId: z.string().nullable(),
  seeded: z.boolean(),
});

const PersistedFileSchema = z.array(PersistedScopeRecordSchema);

type PersistedScopeRecord = z.infer<typeof PersistedScopeRecordSchema>;

// ---- Auth-server API shapes -------------------------------------------------

interface ProjectShape {
  id: string;
  machineId: string;
  projectId: string;
  label: string | null;
  cwd: string;
  createdAt: string;
}

interface StackShape {
  id: string;
  projectId: string;
  userId: string;
  kind: string;
  name: string;
  stackOrder: number;
  createdAt: string;
  archivedAt: string | null;
}

interface TabShape {
  id: string;
  stackId: string;
  projectId: string;
  userId: string;
  targetKind: string;
  targetId: string;
  state: string;
  /** Runtime status stamped by the daemon fan-out (may be stale after a crash). */
  status?: string | null;
  attentionState?: string | null;
}

// ---- Options ----------------------------------------------------------------

export interface TabBackfillOptions {
  /** Auth-server base URL, e.g. "https://auth.appostle.app". */
  authServerUrl: string;
  /** Long-lived Bearer for /daemon/* calls. */
  daemonToken: string;
  /** The auth-server host row ID for this daemon. */
  hostId: string;
  /** The auth-server user ID that owns this daemon. */
  ownerUserId: string;
  /** Initialized workspace registry (source of workspace cwd paths). */
  workspaceRegistry: WorkspaceRegistry;
  /** Initialized agent storage (source of all persisted agents). */
  agentStorage: AgentStorage;
  /** Absolute path to workspace-stacks.json. */
  workspaceStacksFilePath: string;
  /** Optional project registry — when wired, projectDisplayName is synced. */
  projectRegistry?: ProjectRegistry | null;
  logger: Logger;
}

// ---- HTTP helpers -----------------------------------------------------------

async function authServerFetch<T>(
  authServerUrl: string,
  daemonToken: string,
  path: string,
  init?: RequestInit,
): Promise<{ status: number; body: T }> {
  const res = await fetch(`${authServerUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${daemonToken}`,
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as T) : (undefined as unknown as T);
  return { status: res.status, body };
}

// ---- Shared helper: create a tab on the recipient's side --------------------

export interface CreateSharedSessionTabOptions {
  authServerUrl: string;
  daemonToken: string;
  /** UserId being granted access — the tab will live under their account. */
  recipientUserId: string;
  /** Canonical project key (git remote URL or `appostleproj-<hex>` marker). */
  dotfileProjectId: string;
  /** Workspace display name used as the project label on first upsert. */
  workspaceDisplayName: string;
  /** Workspace cwd (absolute path on this machine). */
  workspaceCwd: string;
  /** Agent being shared. */
  agentId: string;
  agentTitle: string | null;
  /** UserId that owns the agent (the one doing the sharing). */
  agentOwnerUserId: string;
  /** LIVE for active agents, ARCHIVED for finished/error/etc. */
  tabState: "LIVE" | "ARCHIVED";
  logger: Logger;
}

export interface CreateSharedSessionTabResult {
  created: boolean;
  /** Auth-server tab id when created; null on dedup / failure. */
  tabId: string | null;
  /** The recipient's `sharedWithMe` stack id (resolved during the call). */
  sharedWithMeStackId: string | null;
}

/**
 * Backfill the recipient's "Shared with me" stack with a tab for the
 * shared agent. Extracted from `runWorkspaceBackfill`'s inner loop so
 * it can be invoked from both the boot-time backfill and the live
 * `AgentManager.shareSession` path.
 *
 * Idempotent: the auth-server upserts (`/daemon/sync/projects`,
 * `/daemon/sync/tabs`) handle dedup, so calling twice with the same
 * inputs is a no-op on the second run.
 */
export async function createSharedSessionTab(
  options: CreateSharedSessionTabOptions,
): Promise<CreateSharedSessionTabResult> {
  const {
    authServerUrl,
    daemonToken,
    recipientUserId,
    dotfileProjectId,
    workspaceDisplayName,
    workspaceCwd,
    agentId,
    agentTitle,
    agentOwnerUserId,
    tabState,
    logger,
  } = options;

  try {
    // 1. Ensure recipient has a project row + their own default stacks.
    const projectRes = await authServerFetch<{ project: ProjectShape }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/projects",
      {
        method: "POST",
        body: JSON.stringify({
          userId: recipientUserId,
          projectId: dotfileProjectId,
          label: workspaceDisplayName,
          cwd: workspaceCwd,
        }),
      },
    );
    if (projectRes.status !== 201) {
      logger.warn(
        { status: projectRes.status, agentId, recipientUserId },
        "shared-session-tab: project upsert failed",
      );
      return { created: false, tabId: null, sharedWithMeStackId: null };
    }
    const authProjectId = projectRes.body.project.id;

    // 2. Fetch the recipient's stacks for that project and find `sharedWithMe`.
    const stacksRes = await authServerFetch<{ stacks: StackShape[] }>(
      authServerUrl,
      daemonToken,
      `/daemon/sync/projects/${authProjectId}/stacks?userId=${recipientUserId}`,
    );
    if (stacksRes.status !== 200) {
      logger.warn(
        { status: stacksRes.status, agentId, recipientUserId },
        "shared-session-tab: stacks fetch failed",
      );
      return { created: false, tabId: null, sharedWithMeStackId: null };
    }
    const sharedWithMeStack = stacksRes.body.stacks.find((s) => s.kind === "sharedWithMe");
    if (!sharedWithMeStack) {
      logger.warn(
        { agentId, recipientUserId },
        "shared-session-tab: recipient has no sharedWithMe stack",
      );
      return { created: false, tabId: null, sharedWithMeStackId: null };
    }

    // 3. Create the tab in the recipient's `sharedWithMe` stack.
    const tabRes = await authServerFetch<{ tab: TabShape }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/tabs",
      {
        method: "POST",
        body: JSON.stringify({
          userId: recipientUserId,
          stackId: sharedWithMeStack.id,
          targetKind: "agent",
          targetId: agentId,
          state: tabState,
          label: agentTitle,
          sharedFromUserId: agentOwnerUserId,
          // A live re-share must revive a previously-archived tab — the
          // recipient is being granted access again. Never set for
          // ARCHIVED shares (boot backfill of finished agents).
          ...(tabState === "LIVE" ? { reviveIfArchived: true } : {}),
        }),
      },
    );

    if (tabRes.status === 201) {
      return {
        created: true,
        tabId: tabRes.body.tab.id,
        sharedWithMeStackId: sharedWithMeStack.id,
      };
    }
    // 200/409 = already existed (idempotent), still return the stack id so
    // the caller can re-focus the existing tab if desired.
    return { created: false, tabId: null, sharedWithMeStackId: sharedWithMeStack.id };
  } catch (err) {
    logger.warn({ err, agentId, recipientUserId }, "shared-session-tab: unexpected error");
    return { created: false, tabId: null, sharedWithMeStackId: null };
  }
}

// ---- Shared helper: create an own-agent tab next to a parent agent's tab ----

export interface CreateOwnAgentTabInheritingStackOptions {
  authServerUrl: string;
  daemonToken: string;
  /** UserId that owns the agent (and will own the tab). */
  ownerUserId: string;
  /** Canonical project key (git remote URL or `appostleproj-<hex>` marker). */
  dotfileProjectId: string;
  /** Workspace display name used as the project label on first upsert. */
  workspaceDisplayName: string;
  /** Workspace cwd (absolute path on this machine). */
  workspaceCwd: string;
  /** The newly-spawned child agent the tab is for. */
  childAgentId: string;
  childAgentTitle: string | null;
  /**
   * Parent agent id whose tab the child should be placed next to. The
   * child inherits the parent's `stackId` so handed-off disciples appear
   * in the same group as their shepherd. Falls back to the owner's
   * default "Sessions" (user) stack if the parent has no LIVE tab.
   */
  parentAgentId: string;
  /** Agent provider name (e.g. "claude") — persisted on the tab row. */
  provider?: string | null;
  logger: Logger;
}

export interface CreateOwnAgentTabInheritingStackResult {
  created: boolean;
  tabId: string | null;
  /** The stack the tab landed in (parent's stack or the owner's Sessions stack). */
  stackId: string | null;
  /** Auth-server project id (resolved during the call). */
  projectId: string | null;
  /**
   * Full serialized tab body from auth-server (`serializeTab()` output).
   * Forwarded inline in the `tab_appeared` WS push so the client can
   * apply it directly without a syncAll round-trip. `null` on failure.
   */
  tabBody: unknown;
}

/**
 * Create an own-agent tab on the auth-server for a child agent spawned by
 * `handoff`, placing it in the same stack as the parent agent's tab.
 *
 * Idempotent — `/daemon/sync/projects` and `/daemon/sync/tabs` both upsert.
 * Calling twice with the same inputs is a no-op on the second run.
 */
export async function createOwnAgentTabInheritingStack(
  options: CreateOwnAgentTabInheritingStackOptions,
): Promise<CreateOwnAgentTabInheritingStackResult> {
  const {
    authServerUrl,
    daemonToken,
    ownerUserId,
    dotfileProjectId,
    workspaceDisplayName,
    workspaceCwd,
    childAgentId,
    childAgentTitle,
    parentAgentId,
    provider,
    logger,
  } = options;

  try {
    // 1. Upsert the project row so we have an auth-server projectId to query.
    const projectRes = await authServerFetch<{ project: ProjectShape }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/projects",
      {
        method: "POST",
        body: JSON.stringify({
          userId: ownerUserId,
          projectId: dotfileProjectId,
          label: workspaceDisplayName,
          cwd: workspaceCwd,
        }),
      },
    );
    if (projectRes.status !== 201) {
      logger.warn(
        { status: projectRes.status, childAgentId, ownerUserId },
        "own-agent-tab: project upsert failed",
      );
      return { created: false, tabId: null, stackId: null, projectId: null, tabBody: null };
    }
    const authProjectId = projectRes.body.project.id;

    // 2. Find the parent's LIVE tab to inherit its stackId.
    const tabsRes = await authServerFetch<{ tabs: TabShape[] }>(
      authServerUrl,
      daemonToken,
      `/daemon/sync/projects/${authProjectId}/tabs?userId=${ownerUserId}`,
    );

    let targetStackId: string | null = null;
    if (tabsRes.status === 200) {
      const parentTab = tabsRes.body.tabs.find(
        (t) => t.targetId === parentAgentId && t.state === "LIVE",
      );
      if (parentTab) {
        targetStackId = parentTab.stackId;
      }
    } else {
      logger.warn(
        { status: tabsRes.status, childAgentId, parentAgentId },
        "own-agent-tab: tabs fetch failed; will fall back to Sessions stack",
      );
    }

    // 3. Fall back to the owner's default user ("Sessions") stack.
    if (!targetStackId) {
      const stacksRes = await authServerFetch<{ stacks: StackShape[] }>(
        authServerUrl,
        daemonToken,
        `/daemon/sync/projects/${authProjectId}/stacks?userId=${ownerUserId}`,
      );
      if (stacksRes.status !== 200) {
        logger.warn(
          { status: stacksRes.status, childAgentId, ownerUserId },
          "own-agent-tab: stacks fetch failed — cannot place tab",
        );
        return {
          created: false,
          tabId: null,
          stackId: null,
          projectId: authProjectId,
          tabBody: null,
        };
      }
      const sessionsStack = stacksRes.body.stacks.find((s) => s.kind === "user");
      if (!sessionsStack) {
        logger.warn(
          { childAgentId, ownerUserId },
          "own-agent-tab: owner has no default Sessions stack",
        );
        return {
          created: false,
          tabId: null,
          stackId: null,
          projectId: authProjectId,
          tabBody: null,
        };
      }
      targetStackId = sessionsStack.id;
    }

    // 4. Create the tab in the resolved stack.
    const tabRes = await authServerFetch<{ tab: TabShape }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/tabs",
      {
        method: "POST",
        body: JSON.stringify({
          userId: ownerUserId,
          stackId: targetStackId,
          targetKind: "agent",
          targetId: childAgentId,
          state: "LIVE",
          label: childAgentTitle,
          provider: provider ?? undefined,
          // Live spawn/promote paths must revive a previously-archived tab
          // for the same agent — it is visibly alive again.
          reviveIfArchived: true,
        }),
      },
    );

    if (tabRes.status === 201) {
      logger.info(
        {
          childAgentId,
          parentAgentId,
          stackId: targetStackId,
          tabId: tabRes.body.tab.id,
          inheritedFromParent: tabsRes.status === 200,
        },
        "own-agent-tab: created in parent's stack",
      );
      return {
        created: true,
        tabId: tabRes.body.tab.id,
        stackId: targetStackId,
        projectId: authProjectId,
        tabBody: tabRes.body.tab,
      };
    }
    logger.warn(
      { childAgentId, parentAgentId, status: tabRes.status },
      "own-agent-tab: tab POST returned non-201",
    );
    return {
      created: false,
      tabId: null,
      stackId: targetStackId,
      projectId: authProjectId,
      tabBody: null,
    };
  } catch (err) {
    logger.warn(
      { err, childAgentId, parentAgentId, ownerUserId },
      "own-agent-tab: unexpected error",
    );
    return { created: false, tabId: null, stackId: null, projectId: null, tabBody: null };
  }
}

// ---- Archive + revoke fan-out -----------------------------------------------

export interface ArchiveAgentTabsAcrossWatchersOptions {
  authServerUrl: string;
  daemonToken: string;
  /** Canonical project key (git remote URL or `appostleproj-<hex>` marker). */
  dotfileProjectId: string;
  /** Auth-server userId that owns the agent — their tab flips to ARCHIVED. */
  agentOwnerUserId: string;
  /**
   * Every sharee on the agent at the moment of archival. Their tabs are
   * **deleted** outright — per the owner-centric UX model, sharees don't
   * get an archive entry; their share boundary ended cleanly.
   */
  sharees: ReadonlyArray<{ userId: string }>;
  agentId: string;
  logger: Logger;
}

/**
 * Fan out a global agent archive across every user with a tab pointing
 * at the agent. Idempotent — re-running on an already-archived agent
 * is a no-op on the auth-server side.
 */
export async function archiveAgentTabsAcrossWatchers(
  options: ArchiveAgentTabsAcrossWatchersOptions,
): Promise<void> {
  const {
    authServerUrl,
    daemonToken,
    dotfileProjectId,
    agentOwnerUserId,
    sharees,
    agentId,
    logger,
  } = options;

  // 1. Owner's tab → ARCHIVED (their historical record).
  try {
    const res = await authServerFetch<unknown>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/tabs/by-target",
      {
        method: "PATCH",
        body: JSON.stringify({
          userId: agentOwnerUserId,
          projectId: dotfileProjectId,
          targetKind: "agent",
          targetId: agentId,
          state: "ARCHIVED",
        }),
      },
    );
    if (res.status !== 200 && res.status !== 404) {
      logger.warn(
        { status: res.status, agentId, agentOwnerUserId },
        "archive fan-out: owner-tab PATCH failed",
      );
    }
  } catch (err) {
    logger.warn({ err, agentId, agentOwnerUserId }, "archive fan-out: owner-tab error");
  }

  // 2. Every sharee's tab → DELETED. No archive history for them.
  for (const share of sharees) {
    try {
      const res = await authServerFetch<unknown>(
        authServerUrl,
        daemonToken,
        "/daemon/sync/tabs/by-target",
        {
          method: "DELETE",
          body: JSON.stringify({
            userId: share.userId,
            projectId: dotfileProjectId,
            targetKind: "agent",
            targetId: agentId,
          }),
        },
      );
      if (res.status !== 204 && res.status !== 404) {
        logger.warn(
          { status: res.status, agentId, shareeUserId: share.userId },
          "archive fan-out: sharee-tab DELETE failed",
        );
      }
    } catch (err) {
      logger.warn(
        { err, agentId, shareeUserId: share.userId },
        "archive fan-out: sharee-tab error",
      );
    }
  }
}

export interface DeleteSharedSessionTabOptions {
  authServerUrl: string;
  daemonToken: string;
  dotfileProjectId: string;
  /** UserId whose tab should be removed. */
  recipientUserId: string;
  agentId: string;
  logger: Logger;
}

/**
 * Remove a single sharee's tab on the auth-server. Used by the revoke
 * fan-out when a sharee closes their Shared-with-me tab — the agent
 * itself stays alive, and only that sharee loses their view.
 *
 * Idempotent. 204 / 404 both treated as success.
 */
export async function deleteSharedSessionTab(
  options: DeleteSharedSessionTabOptions,
): Promise<void> {
  const { authServerUrl, daemonToken, dotfileProjectId, recipientUserId, agentId, logger } =
    options;
  try {
    const res = await authServerFetch<unknown>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/tabs/by-target",
      {
        method: "DELETE",
        body: JSON.stringify({
          userId: recipientUserId,
          projectId: dotfileProjectId,
          targetKind: "agent",
          targetId: agentId,
        }),
      },
    );
    if (res.status !== 204 && res.status !== 404) {
      logger.warn(
        { status: res.status, agentId, recipientUserId },
        "revoke fan-out: sharee-tab DELETE failed",
      );
    }
  } catch (err) {
    logger.warn({ err, agentId, recipientUserId }, "revoke fan-out: sharee-tab error");
  }
}

// ---- Project removal fan-out ------------------------------------------------

export interface DeleteProjectOnAuthServerOptions {
  authServerUrl: string;
  daemonToken: string;
  /** Canonical project key (git remote URL or `appostleproj-<hex>` marker). */
  projectKey?: string;
  /** Workspace cwd. Used as a legacy-safe fallback when project identity changed. */
  cwd?: string;
  logger: Logger;
}

/**
 * Remove a project (and all its stacks + tabs, for every user) on the
 * auth-server when the workspace is archived/removed on the daemon.
 * Idempotent — a key with no rows is a 204 no-op on the server.
 */
export async function deleteProjectOnAuthServer(
  options: DeleteProjectOnAuthServerOptions,
): Promise<boolean> {
  const { authServerUrl, daemonToken, projectKey, cwd, logger } = options;
  if (!projectKey && !cwd) {
    logger.warn("project removal fan-out skipped: no project key or cwd");
    return false;
  }
  try {
    const res = await authServerFetch<{ deletedProjects?: number }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/projects/by-key",
      {
        method: "DELETE",
        body: JSON.stringify({ projectId: projectKey, cwd }),
      },
    );
    // 204 = pre-count daemons hitting an updated server, or vice versa.
    if (res.status !== 200 && res.status !== 204) {
      logger.warn({ status: res.status, projectKey, cwd }, "project removal fan-out failed");
      return false;
    }
    const deleted = res.body?.deletedProjects ?? 0;
    if (deleted > 0) {
      logger.info({ projectKey, cwd, deleted }, "removed project from auth-server");
    }
    return true;
  } catch (err) {
    logger.warn({ err, projectKey, cwd }, "project removal fan-out error");
    return false;
  }
}

// ---- Legacy dotfile helpers -------------------------------------------------

/**
 * Read the RETIRED `.appostle/project-id` dotfile, if one still exists.
 * Never creates one — the canonical project key is resolved via
 * `resolveCanonicalProjectId` (git remote / `appostleproj-<hex>` marker).
 * The value is only used to re-key existing auth-server project rows during
 * the boot backfill, after which the dotfile is deleted.
 */
export async function readLegacyProjectIdDotfile(workspaceCwd: string): Promise<string | null> {
  const dotFile = path.join(workspaceCwd, ".appostle", "project-id");
  try {
    const existing = await fs.readFile(dotFile, "utf8");
    const trimmed = existing.trim();
    return trimmed || null;
  } catch {
    return null;
  }
}

/** Remove the retired `.appostle/project-id` dotfile. ENOENT-safe. */
export async function deleteLegacyProjectIdDotfile(workspaceCwd: string): Promise<void> {
  const dotFile = path.join(workspaceCwd, ".appostle", "project-id");
  try {
    await fs.unlink(dotFile);
  } catch {
    // already gone or unwritable — fine either way
  }
}

// ---- Stack scope reader -----------------------------------------------------

/**
 * Read workspace-stacks.json and return all scope records. Returns [] if the
 * file doesn't exist yet.
 */
async function readWorkspaceStacksFile(filePath: string): Promise<PersistedScopeRecord[]> {
  try {
    const raw = await fs.readFile(filePath, "utf8");
    return PersistedFileSchema.parse(JSON.parse(raw));
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === "ENOENT") return [];
    throw err;
  }
}

// ---- Per-workspace backfill -------------------------------------------------

export interface WorkspaceBackfillOptions {
  /** Auth-server base URL. */
  authServerUrl: string;
  /** Long-lived Bearer for /daemon/* calls. */
  daemonToken: string;
  /** The auth-server user ID that owns this daemon. */
  ownerUserId: string;
  /** The workspace to backfill. */
  workspace: PersistedWorkspaceRecord;
  /** Pre-fetched agent list (or undefined to fetch internally). */
  agentStorage: AgentStorage;
  /** Absolute path to workspace-stacks.json. */
  workspaceStacksFilePath: string;
  /**
   * Optional — when wired, the workspace's project displayName is looked up
   * and passed to the auth-server project upsert so Recents can render the
   * curated name instead of falling back to the workspace label. Part of
   * retire-sessions-projection step 2.
   */
  projectRegistry?: ProjectRegistry | null;
  logger: Logger;
}

export interface WorkspaceBackfillResult {
  /** True if the project upsert succeeded; false if the workspace was skipped. */
  processed: boolean;
  /** Number of tabs created (owner + shared-with-me). */
  tabsCreated: number;
}

/**
 * Backfill a single workspace. Idempotent: re-running on an already-backfilled
 * workspace is a no-op (the auth-server upserts handle the dedup). Safe to call
 * lazily on workspace registration as well as in bulk at boot.
 */
export async function runWorkspaceBackfill(
  options: WorkspaceBackfillOptions,
): Promise<WorkspaceBackfillResult> {
  const {
    authServerUrl,
    daemonToken,
    ownerUserId,
    workspace,
    agentStorage,
    workspaceStacksFilePath,
    projectRegistry,
    logger,
  } = options;

  if (workspace.archivedAt) {
    // Archived == removed from Appostle. The cloud entry (project row +
    // stacks + tabs) must not linger in recents — delete it on every boot,
    // so rows from before the live removal fan-out existed self-heal here.
    // Read-only key resolution: never mint a marker in a removed folder.
    // Delete under every key the row could have been created with (canonical
    // key, retired dotfile id, registry id) — all idempotent 204s.
    const archiveLogger = logger.child({ cwd: workspace.cwd, archived: true });
    const candidateKeys = new Set<string>();
    const peeked = await peekCanonicalProjectId(workspace.cwd).catch(() => null);
    if (peeked) candidateKeys.add(peeked);
    const legacyProjectId = await readLegacyProjectIdDotfile(workspace.cwd);
    if (legacyProjectId) candidateKeys.add(legacyProjectId);
    if (workspace.projectId) candidateKeys.add(workspace.projectId);
    for (const projectKey of candidateKeys) {
      await deleteProjectOnAuthServer({
        authServerUrl,
        daemonToken,
        projectKey,
        cwd: workspace.cwd,
        logger: archiveLogger,
      });
    }
    if (legacyProjectId) {
      await deleteLegacyProjectIdDotfile(workspace.cwd);
    }
    return { processed: false, tabsCreated: 0 };
  }

  const wsLogger = logger.child({
    cwd: workspace.cwd,
    workspaceId: workspace.workspaceId,
  });

  // Load per-workspace data lazily (cheap relative to the HTTP calls below).
  const [allAgents, stacksFileRecords] = await Promise.all([
    agentStorage.list(),
    readWorkspaceStacksFile(workspaceStacksFilePath),
  ]);

  const workspaceAgents = allAgents.filter((a) => a.cwd === workspace.cwd);
  const oldScopeRecords = stacksFileRecords.filter(
    (r) => r.workspaceId === workspace.workspaceId || r.workspaceId === workspace.cwd,
  );

  let tabsCreated = 0;

  try {
    // Step 1: Resolve the canonical project key (git remote / appostleproj marker).
    // A legacy key (if any) rides along so the auth-server re-keys the existing
    // project row in place instead of forking a duplicate.
    const legacyProjectId = await readLegacyProjectIdDotfile(workspace.cwd);
    const dotfileProjectId = await resolveCanonicalProjectId(workspace.cwd);
    if (!dotfileProjectId) {
      // The workspace directory is gone or unreadable — there is no stable key
      // to upsert under. Skip rather than mint a UUID-keyed row that the
      // runtime fan-out can never address.
      wsLogger.warn("could not resolve canonical project key — skipping workspace");
      return { processed: false, tabsCreated: 0 };
    }
    // A stale marker means this folder used to be non-git and later gained a
    // git remote — the marker is the legacy key the auth-server must re-key
    // from (project row AND todos), then the marker file gets removed.
    const staleMarker = await readProjectMarker(workspace.cwd);
    const markerLegacy = staleMarker && staleMarker !== dotfileProjectId ? staleMarker : null;
    // Rows may have been created under the dotfile-era id, the stale marker,
    // or (via the retired UUID fallback) the registry project id.
    const rekeyFromProjectId = legacyProjectId ?? markerLegacy ?? workspace.projectId;

    // Step 2: Upsert the project on the auth-server for the owner user.
    // This also auto-creates the 3 canonical stacks for ownerUserId.
    let projectDisplayName: string | null = null;
    if (projectRegistry) {
      try {
        const project = await projectRegistry.get(workspace.projectId);
        projectDisplayName = project?.displayName ?? null;
      } catch (err) {
        wsLogger.debug({ err }, "projectRegistry lookup failed — leaving displayName null");
      }
    }
    const projectRes = await authServerFetch<{ project: ProjectShape }>(
      authServerUrl,
      daemonToken,
      "/daemon/sync/projects",
      {
        method: "POST",
        body: JSON.stringify({
          userId: ownerUserId,
          projectId: dotfileProjectId,
          legacyProjectId: rekeyFromProjectId !== dotfileProjectId ? rekeyFromProjectId : undefined,
          label: workspace.displayName,
          cwd: workspace.cwd,
          displayName: projectDisplayName,
        }),
      },
    );

    if (projectRes.status !== 201) {
      wsLogger.warn(
        { status: projectRes.status, body: projectRes.body },
        "project upsert failed — skipping workspace",
      );
      return { processed: false, tabsCreated };
    }

    // The auth-server row is now keyed by the canonical project key — the
    // legacy artifacts have served their migration purpose. Delete them so
    // nothing recreates the old identity.
    if (legacyProjectId && legacyProjectId !== dotfileProjectId) {
      await deleteLegacyProjectIdDotfile(workspace.cwd);
      wsLogger.info(
        { legacyProjectId, projectKey: dotfileProjectId },
        "migrated legacy project-id dotfile to canonical project key",
      );
    }
    if (markerLegacy) {
      await deleteProjectMarker(workspace.cwd, markerLegacy);
      wsLogger.info(
        { marker: markerLegacy, projectKey: dotfileProjectId },
        "migrated marker-keyed project to git remote key",
      );
    }

    const authProjectId = projectRes.body.project.id; // auth-server row id (prj_...)

    // Step 3: Get the stacks for the owner user so we know the Sessions stack id
    const stacksRes = await authServerFetch<{ stacks: StackShape[] }>(
      authServerUrl,
      daemonToken,
      `/daemon/sync/projects/${authProjectId}/stacks?userId=${ownerUserId}`,
    );

    if (stacksRes.status !== 200) {
      wsLogger.warn({ status: stacksRes.status }, "could not fetch stacks — skipping workspace");
      return { processed: false, tabsCreated };
    }

    const ownerStacks = stacksRes.body.stacks;
    const ownerSessionsStack = ownerStacks.find((s) => s.kind === "user") ?? null;

    // Build a map from old daemon stack ID → auth-server stack ID for this user+workspace.
    // We only migrate non-starter user stacks.
    const oldToNewStackId = new Map<string, string>();
    const ownerScopeRecord = oldScopeRecords.find((r) => r.userId === ownerUserId);

    if (ownerScopeRecord) {
      for (const oldStack of ownerScopeRecord.stacks) {
        if (STARTER_STACK_NAMES.has(oldStack.name)) continue; // skip paseo-era starters

        // Migrate this user-created stack to the auth-server
        const createStackRes = await authServerFetch<{ stack: StackShape }>(
          authServerUrl,
          daemonToken,
          "/daemon/sync/stacks",
          {
            method: "POST",
            body: JSON.stringify({
              userId: ownerUserId,
              projectId: authProjectId,
              kind: "user",
              name: oldStack.name,
              stackOrder: oldStack.order,
            }),
          },
        );

        if (createStackRes.status === 201) {
          oldToNewStackId.set(oldStack.id, createStackRes.body.stack.id);
        } else {
          wsLogger.warn(
            { stackName: oldStack.name, status: createStackRes.status },
            "could not migrate user stack",
          );
        }
      }
    }

    // Step 4: Migrate agents for this workspace to Tabs
    for (const agent of workspaceAgents) {
      if (agent.internal) continue; // Never create Tabs for internal agents
      if (!agent.id) continue;
      // Archived agents already had their archive fan-out (owner tab →
      // ARCHIVED, sharee tabs → deleted). Recreating rows here would
      // resurrect tabs the user explicitly closed every time a folder is
      // re-added. Finished-but-not-archived agents still get ARCHIVED
      // tabs below.
      if (agent.archivedAt) continue;

      const agentOwnerUserId = agent.ownerUserId ?? ownerUserId;

      // Determine which stack to put this tab in
      let targetStackId: string | null = null;

      if (agent.stackId) {
        // Agent has an explicit stack assignment — map to new stack id
        targetStackId = oldToNewStackId.get(agent.stackId) ?? null;
      }

      // If no mapped stack, fall back to the owner's Sessions stack
      if (!targetStackId) {
        targetStackId = ownerSessionsStack?.id ?? null;
      }

      if (!targetStackId) {
        wsLogger.warn({ agentId: agent.id }, "no Sessions stack found — skipping agent");
        continue;
      }

      // Determine state: LIVE if agent has an active last status, else ARCHIVED
      const isActive =
        agent.lastStatus === "running" ||
        agent.lastStatus === "initializing" ||
        agent.lastStatus === "idle";
      const tabState = isActive ? "LIVE" : "ARCHIVED";

      // Create tab for the owner user
      try {
        const tabRes = await authServerFetch<{ tab: TabShape }>(
          authServerUrl,
          daemonToken,
          "/daemon/sync/tabs",
          {
            method: "POST",
            body: JSON.stringify({
              userId: agentOwnerUserId,
              stackId: targetStackId,
              targetKind: "agent",
              targetId: agent.id,
              state: tabState,
              label: agent.title ?? null,
              // Persist provider so archived tabs can render their provider
              // icon without a live daemon.
              provider: agent.provider ?? undefined,
            }),
          },
        );

        if (tabRes.status === 201) {
          tabsCreated++;
        } else {
          wsLogger.warn(
            { agentId: agent.id, status: tabRes.status },
            "could not create Tab for agent",
          );
        }
      } catch (err) {
        wsLogger.warn({ agentId: agent.id, err }, "error creating Tab for agent");
      }

      // Step 5: Create Tabs for session-share recipients (factored into
      // `createSharedSessionTab` so the live `shareSession` path can
      // reuse the same logic without duplicating the auth-server dance).
      if (agent.sessionShares && agent.sessionShares.length > 0) {
        // Shared-tab state is decoupled from owner-tab state. The owner's
        // tab follows agent activity (running/idle → LIVE, anything else
        // → ARCHIVED) which is reasonable for their own session list. The
        // recipient's tab should persist in `Shared with me` regardless
        // of whether the agent finished its turn — only `archivedAt`
        // (the daemon-side "this agent has been closed for good"
        // signal) collapses the share to ARCHIVED. Matches the product
        // model: shared sessions are a clean record for the recipient
        // until either party explicitly tears the share down.
        const sharedTabState: "LIVE" | "ARCHIVED" = agent.archivedAt ? "ARCHIVED" : "LIVE";
        for (const share of agent.sessionShares) {
          const result = await createSharedSessionTab({
            authServerUrl,
            daemonToken,
            recipientUserId: share.userId,
            dotfileProjectId,
            workspaceDisplayName: workspace.displayName,
            workspaceCwd: workspace.cwd,
            agentId: agent.id,
            agentTitle: agent.title ?? null,
            agentOwnerUserId,
            tabState: sharedTabState,
            logger: wsLogger,
          });
          if (result.created) tabsCreated++;
        }
      }
    }

    // Step 6: Stuck-status recovery. If the daemon died while an agent was
    // running, the cloud tab row keeps status "running" forever — the agent
    // restore path never re-fires emitState for agents that come back idle
    // with no activity. The daemon just booted, so nothing can actually be
    // running yet: reconcile any tab row still claiming a live runtime
    // status down to the restored agent's persisted status (or "idle").
    try {
      const tabsListRes = await authServerFetch<{ tabs: TabShape[] }>(
        authServerUrl,
        daemonToken,
        `/daemon/sync/projects/${authProjectId}/tabs?userId=${ownerUserId}`,
      );
      if (tabsListRes.status === 200) {
        const agentsById = new Map(workspaceAgents.map((a) => [a.id, a]));
        for (const tab of tabsListRes.body.tabs) {
          if (tab.targetKind !== "agent") continue;
          if (tab.status !== "running" && tab.status !== "initializing") continue;
          const stored = agentsById.get(tab.targetId);
          // A persisted "running"/"initializing" (or a missing agent record)
          // means the daemon died mid-turn — the truthful post-boot status
          // is idle. Persisted terminal states win as-is.
          const reconciledStatus =
            stored && stored.lastStatus !== "running" && stored.lastStatus !== "initializing"
              ? stored.lastStatus
              : "idle";
          const patchRes = await authServerFetch<unknown>(
            authServerUrl,
            daemonToken,
            "/daemon/sync/tabs/by-target/runtime",
            {
              method: "PATCH",
              body: JSON.stringify({
                userId: tab.userId,
                projectId: dotfileProjectId,
                targetKind: "agent",
                targetId: tab.targetId,
                status: reconciledStatus,
                // Pending permission prompts did not survive the restart.
                pendingPermissionCount: 0,
                ...(tab.attentionState === "permission-prompt"
                  ? { attentionState: "none" as const }
                  : {}),
              }),
            },
          );
          if (patchRes.status !== 200 && patchRes.status !== 404) {
            wsLogger.warn(
              { agentId: tab.targetId, status: patchRes.status },
              "stuck-status reconcile: runtime patch failed",
            );
          } else {
            wsLogger.info(
              { agentId: tab.targetId, from: tab.status, to: reconciledStatus },
              "stuck-status reconcile: cleared stale live status",
            );
          }
        }
      } else {
        wsLogger.warn(
          { status: tabsListRes.status },
          "stuck-status reconcile: tabs fetch failed — skipping",
        );
      }
    } catch (err) {
      wsLogger.warn({ err }, "stuck-status reconcile: unexpected error");
    }

    wsLogger.info(
      { authProjectId, tabsInWorkspace: workspaceAgents.filter((a) => !a.internal).length },
      "workspace backfilled",
    );
    return { processed: true, tabsCreated };
  } catch (err) {
    wsLogger.error({ err }, "unexpected error backfilling workspace");
    return { processed: false, tabsCreated };
  }
}

// ---- Boot-time backfill (iterates over all workspaces) ---------------------

export async function runTabBackfill(options: TabBackfillOptions): Promise<void> {
  const {
    authServerUrl,
    daemonToken,
    ownerUserId,
    workspaceRegistry,
    agentStorage,
    workspaceStacksFilePath,
    projectRegistry,
    logger,
  } = options;

  const backfillLogger = logger.child({ component: "tab-backfill" });
  backfillLogger.info("starting tab backfill");

  const workspaces = await workspaceRegistry.list();
  backfillLogger.info({ workspaceCount: workspaces.length }, "loaded workspaces");

  let projectsProcessed = 0;
  let tabsCreated = 0;

  for (const workspace of workspaces) {
    const result = await runWorkspaceBackfill({
      authServerUrl,
      daemonToken,
      ownerUserId,
      workspace,
      agentStorage,
      workspaceStacksFilePath,
      projectRegistry,
      logger: backfillLogger,
    });
    if (result.processed) projectsProcessed++;
    tabsCreated += result.tabsCreated;
  }

  backfillLogger.info({ projectsProcessed, tabsCreated }, "tab backfill complete");
}
